import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';
import { createDrawerMotion } from '@theme/header-drawer-motion';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * GSAP motion controller for the root drawer, or null when motion is unavailable
   * (no GSAP / reduced motion / mobile). When null, the stock CSS transition is used.
   * @type {{ open: (onComplete?: () => void) => void, close: (onComplete?: () => void) => void, showSubmenuToolbar?: (submenuDetails: HTMLElement) => void, showTopbarToolbar?: (submenuDetails: HTMLElement) => void, destroy?: () => void, isAnimating?: boolean } | null}
   */
  #motion = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMotion();
  }

  /**
   * Initialize the GSAP drawer motion once GSAP is available. GSAP loads deferred,
   * so if it is not ready yet we wait for the window load event before wiring up.
   */
  #setupMotion() {
    const init = () => {
      if (!window.gsap || this.#motion) return;
      this.#motion = createDrawerMotion(this.refs.menuDrawer, this.querySelector('.menu-drawer__backdrop'));
    };

    init();

    if (!this.#motion) {
      window.addEventListener('load', init, { once: true });
      requestAnimationFrame(() => requestAnimationFrame(init));
    }
  }

  disconnectedCallback() {
    this.#motion?.destroy?.();
    this.#motion = null;
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    if (this.#motion?.isAnimating) return;
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * @returns {boolean}
   */
  #isMobileDrawer() {
    return window.matchMedia('(max-width: 1024px)').matches;
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    if (this.#motion?.isAnimating) return;

    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    if (target) {
      this.#resetDrawerScroll(details);
    }

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.setAttribute('open', '');
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');

        if (this.#motion?.showSubmenuToolbar) {
          this.#motion.showSubmenuToolbar(/** @type {HTMLElement} */ (details));
        }

        if (this.#isMobileDrawer()) {
          trapFocus(details);
          return;
        }
      }

      // GSAP owns the root drawer open motion; submenu drill-ins keep the CSS transition.
      if (this.#motion && details === this.refs.details && !target) {
        this.#motion.open(() => trapFocus(details));
        return;
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    if (this.#motion?.isAnimating && details === this.refs.details) return;

    const summary = details.querySelector('summary');

    if (!summary) return;

    const isSubmenuBack = details !== this.refs.details;

    if (isSubmenuBack && this.#isMobileDrawer() && this.#motion?.showTopbarToolbar) {
      this.#motion.showTopbarToolbar(/** @type {HTMLElement} */ (details));
      summary.setAttribute('aria-expanded', 'false');
      details.classList.remove('menu-open');
      this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');
      reset(details);
      trapFocus(this.refs.details);
      return;
    }

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    const finishClose = () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    };

    // GSAP owns the root drawer close motion; submenu back keeps the CSS transition.
    if (this.#motion && details === this.refs.details) {
      this.#motion.close(finishClose);
      return;
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(drawer || details, finishClose, { subtree: false });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Snap the drawer (and nested submenu panels) back to the top before drill-in
   * so a scrolled main list does not clip the top bar on the submenu view.
   * @param {HTMLDetailsElement} details
   */
  #resetDrawerScroll(details) {
    this.refs.menuDrawer.scrollTo({ top: 0, behavior: 'instant' });

    const submenu = details.querySelector('.menu-drawer__submenu');
    submenu?.scrollTo({ top: 0, behavior: 'instant' });
    submenu?.querySelector('.menu-drawer__inner-submenu')?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
